export type LeadInsuranceType = 'pet' | 'renters' | 'car' | 'home';

const GA_MEASUREMENT_ID = 'G-0HM2YYP919';

export type LeadFunnelStep =
  | 'form_view'
  | 'form_submit'
  | 'signup_redirect'
  | 'signup_complete'
  | 'verify_email_view'
  | 'email_verified'
  | 'login_complete'
  | 'lead_generated'
  | 'car_lead_submit'
  | 'car_thank_you'
  | 'car_enrichment_start';

/** @deprecated Use LeadFunnelStep */
export type PetLeadFunnelStep = LeadFunnelStep;

/** True when the user is mid pet quote signup (form saved, not yet submitted). */
export function isPetLeadFlow(): boolean {
  return (
    sessionStorage.getItem('insuranceType') === 'pet' ||
    sessionStorage.getItem('petInsuranceFormData') !== null
  );
}

/** True when the user is mid car quote signup (form saved, not yet submitted). */
export function isCarLeadFlow(): boolean {
  return (
    sessionStorage.getItem('insuranceType') === 'car' ||
    sessionStorage.getItem('carInsuranceFormData') !== null ||
    sessionStorage.getItem('leadId') !== null ||
    sessionStorage.getItem('carLeadId') !== null
  );
}

function trackLeadFunnelStep(
  funnelName: 'pet_lead' | 'car_lead',
  insuranceType: LeadInsuranceType,
  step: LeadFunnelStep,
  params: Record<string, string> = {}
): void {
  if (typeof window.gtag !== 'function') return;

  window.gtag('event', 'funnel_step', {
    send_to: GA_MEASUREMENT_ID,
    funnel_name: funnelName,
    funnel_step: step,
    insurance_type: insuranceType,
    ...params,
  });
}

/** Track pet lead funnel progress — build a Funnel exploration in GA4 on event name `funnel_step`. */
export function trackFunnelStep(
  step: LeadFunnelStep,
  params: Record<string, string> = {}
): void {
  trackLeadFunnelStep('pet_lead', 'pet', step, params);
}

/** Track car lead funnel progress — build a Funnel exploration in GA4 on event name `funnel_step`. */
export function trackCarFunnelStep(
  step: LeadFunnelStep,
  params: Record<string, string> = {}
): void {
  trackLeadFunnelStep('car_lead', 'car', step, params);
}

/** Fires GA4 generate_lead — import this event in Google Ads as a conversion. */
export function trackGenerateLead(insuranceType: LeadInsuranceType): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window.gtag !== 'function') {
      if (import.meta.env.DEV) {
        console.warn('[analytics] gtag unavailable — generate_lead not sent', { insuranceType });
      }
      resolve();
      return;
    }

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    if (insuranceType === 'pet') {
      trackFunnelStep('lead_generated');
    }
    if (insuranceType === 'car') {
      trackCarFunnelStep('lead_generated');
    }

    window.gtag('event', 'generate_lead', {
      send_to: GA_MEASUREMENT_ID,
      insurance_type: insuranceType,
      currency: 'USD',
      value: 1,
      event_callback: finish,
      event_timeout: 2000,
    });

    // Fallback if GA never invokes the callback (blocked tag, offline, etc.)
    window.setTimeout(finish, 2000);
  });
}
